import { paymentPath, paymentLabel, money } from '../../utils/directPayment';
import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { get_order } from "../../store/reducers/orderReducer";
import { formatCurrency, fetchExchangeRates } from "../../utils/currencyUtils";
import api from "../../api/api";

const Order = () => {
  const { orderId } = useParams();
  const dispatch = useDispatch();
  const { myOrder, sellerOrders } = useSelector((state) => state.order);
  const { userInfo } = useSelector((state) => state.auth);
  const [exchangeRates, setExchangeRates] = useState({});
  const [currencyCode, setCurrencyCode] = useState("NGN");
  const [exchangeLoaded, setExchangeLoaded] = useState(false);

  useEffect(() => {
    dispatch(get_order(orderId));
    fetchExchangeRates().then((rates) => setExchangeRates(rates));
  }, [orderId]);

  useEffect(() => {
    fetchExchangeRates().then(() => setExchangeLoaded(true));
  }, []);

  useEffect(() => {
    const fetchUserDetails = async () => {
      if (userInfo && userInfo.id) {
        try {
          const { data } = await api.get("/account/get-details");
          if (data.currency) {
            setCurrencyCode(data.currency);
          }
        } catch (error) {
          console.error("Error fetching user currency:", error);
        }
      }
    };
    fetchUserDetails();
  }, [userInfo]);

  const OrderHeader = () => (
    <div className="pb-4">
      <h2 className="text-gray-200 font-semibold flex items-center gap-2">
        <span>Order #{myOrder._id}</span>
        <span className="text-muted-foreground text-sm">{myOrder.date}</span>
      </h2>
    </div>
  );

  const CustomerInfo = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
      <div className="space-y-3">
        <h2 className="text-gray-200 font-semibold">Delivery Information</h2>
        <div className="space-y-2">
          <p className="text-gray-300">{myOrder.shippingInfo?.name}</p>
          <div className="flex items-center gap-2">
            <span className="bg-[#FFE11B] text-black text-xs font-medium px-3 py-1 rounded">
              Home
            </span>
            <span className="text-muted-foreground text-sm">
              {myOrder.shippingInfo?.address}, {myOrder.shippingInfo?.province},{" "}
              {myOrder.shippingInfo?.city}, {myOrder.shippingInfo?.area}
            </span>
          </div>
          <p className="text-muted-foreground text-sm">Email: {userInfo.email}</p>
        </div>
      </div>
      <div className="space-y-3">
        <h2 className="text-gray-200 font-semibold">Order Details</h2>
        <div className="space-y-2">
          <p className="text-gray-300">Total Price: {myOrder.payment_method === "manual_bank_transfer" ? money(Math.round((myOrder.price + (myOrder.shipping_fee || 0)) * 100)) : formatCurrency(myOrder.price, currencyCode, myOrder.products && myOrder.products[0] && myOrder.products[0].currency ? myOrder.products[0].currency : 'USD')} shipping included.</p>
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground">Payment Status:</span>
              <span
                className={`py-1 text-xs px-3 rounded-md ${
                  myOrder.payment_status === "paid"
                    ? "bg-green-600 text-white"
                    : "bg-red-600 text-white"
                }`}
              >
                {paymentLabel(myOrder.payment_status)}
              </span>
            </div>
           
          </div>
        </div>
      </div>
    </div>
  );

 const ProductList = () => (
    <div className="mt-6">
      {myOrder.payment_method === 'manual_bank_transfer' && <a className="inline-block underline mb-4" href={paymentPath(myOrder)}>Bank transfer to merchant — view payments and receipts</a>}
      <h2 className="text-gray-200 text-lg font-semibold mb-4">Products</h2>
      <div className="space-y-6">
        {sellerOrders?.map((sellerOrder, index) => (
          <SellerOrderSection 
            key={index}
            sellerOrder={sellerOrder}
            products={myOrder.products?.filter(p => p.sellerId === sellerOrder.sellerId)}
          />
        ))}
      </div>
    </div>
  );

  const SellerOrderSection = ({ sellerOrder, products }) => (
    <div className="">
      <div className="flex justify-between items-center mb-4">
        <div className="space-y-1">
          <Link to={`/${products[0]?.shopName}`} className="text-gray-200 font-medium hover:text-green-500 transition-colors">
            {products[0]?.shopName}
          </Link>
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground text-sm">Delivery Status:</span>
            <span
              className={`py-1 text-xs px-3 rounded-md ${
                sellerOrder.delivery_status === "Delivered"
                  ? "bg-green-600 text-white"
                  : sellerOrder.delivery_status === "Processing"
                  ? "bg-blue-600 text-white"
                  : "bg-white text-black"
              }`}
            >
              {sellerOrder.delivery_status}
            </span>
          </div>
        </div>
      </div>
      <div className="space-y-4">
        {products?.map((product, index) => (
          <ProductCard key={index} product={product} />
        ))}
      </div>
    </div>
  );

  const ProductCard = ({ product }) => (
    <div className="rounded-lg">
      <div className="flex justify-between items-center">
        <div className="flex gap-4">
          <img
            className="w-16 h-16 rounded-md object-cover"
            src={product.images[0]}
            alt={product.name}
          />
          <div className="flex flex-col justify-center">
            <Link className="text-gray-200 hover:text-blue-400 transition-colors">
              {product.name}
            </Link>
            <div className="text-sm text-muted-foreground space-y-1">
              <p>Brand: {product.brand}</p>
              <p>Quantity: {product.quantity}</p>
            </div>
          </div>
        </div>
        <div className="text-right">
          <h2 className="text-white font-semibold">
            {formatCurrency(product.price - Math.floor((product.price * product.discount) / 100), currencyCode, product.currency)}
          </h2>
          <p className="text-muted-foreground text-sm line-through">{formatCurrency(product.price, currencyCode, product.currency)}</p>
          <p className="text-green-400 text-sm">-{product.discount}%</p>
        </div>
      </div>
    </div>
  );

  return (
    <div className="bg-[#070707] rounded-lg p-6 shadow-lg">
      <OrderHeader />
      <CustomerInfo />
      <ProductList />
    </div>
  );

};

export default Order;
